/* ============================================================
   The print-only front matter of a board pack.

   Everything here is `print-only`: it does not appear on screen, which
   is deliberate. On screen the operator already knows what they typed
   and does not need it read back. On paper the reader was not in the
   room, and the inputs are the first thing they will challenge.

   Ordering is the whole design. Reference, then what was assumed, then
   what was missing, then what the comparison was against — and only
   then the findings the printed page already carries. A director who
   stops reading after page one should still know what this analysis
   rests on.
   ============================================================ */

import { html, raw } from '../shared/html.js';
import {
  CORRIDOR_RUN_REGISTER,
  assumptionsRegister,
  benchmarkBasis,
  completeness,
  documentReference
} from '../data/board-pack.js';
import { shortDate } from '../shared/format.js';

const PROVENANCE_LABEL = {
  'published-tariff': 'Published tariff — taken directly from an operator’s published rate',
  derived: 'Derived — computed from published guidance, with the arithmetic stated',
  contributed: 'Contributed — median of opt-in operator submissions'
};

function money(value, currency) {
  return Number.isFinite(value) ? `${currency} ${value.toFixed(3)}` : '—';
}

/**
 * @param {object} result an assessed run
 * @param {object} options
 * @param {Array}  options.register the tool's own field list
 * @param {string} options.prefix   document reference prefix
 * @param {object} options.input    where the inputs live, if not result.input
 * @returns {HTMLElement} a print-only section
 */
export function BoardPreamble(result, options = {}) {
  const input = options.input || result?.input || {};
  const register = options.register || CORRIDOR_RUN_REGISTER;
  /* Only a corridor run is measured against a published band. A
     financeability assessment is scored against thresholds that live in
     its own engine, so it has no basis section rather than an empty
     one — a heading with nothing under it reads as "we checked and
     found no gap". */
  const showBasis = options.showBasis ?? !options.register;
  const basis = showBasis ? benchmarkBasis(result) : null;
  const rows = assumptionsRegister(input, result?.cost?.benchmark, register);
  const cover = completeness({ ...result, input }, register);
  const reference = documentReference(input, options.prefix || 'KLA', register);

  const node = document.createElement('section');
  node.className = 'print-only board-preamble';

  node.innerHTML = html`
    <dl class="board-meta">
      <div><dt>Document reference</dt><dd>${reference || 'Not derivable'}</dd></div>
      <div><dt>Prepared</dt><dd>${shortDate(new Date().toISOString())}</dd></div>
      <div>
        <dt>Inputs supplied</dt>
        <dd>${cover.supplied} of ${cover.total}</dd>
      </div>
    </dl>
    <p class="board-note">
      The reference above is derived from the figures in this run, not from the time it was
      produced. The same figures always give the same reference; change one and it changes.
      Re-running these inputs reproduces this document.
    </p>

    <h2>Assumptions</h2>
    <p class="board-note">
      Every figure this analysis accepts, whether or not one was given. Nothing absent has been
      substituted with a default or an average — where a figure is not supplied, the findings
      that depend on it are withheld rather than estimated.
    </p>
    <table class="board-table">
      <thead>
        <tr><th>Input</th><th>Value</th><th>Origin</th></tr>
      </thead>
      <tbody>
        ${rows.map(
          (r) => html`<tr class="${r.supplied ? '' : 'row-absent'}">
            <td>${r.label}</td>
            <td class="num">${r.value}</td>
            <td>${r.origin}</td>
          </tr>`
        )}
      </tbody>
    </table>

    ${cover.withheld.length
      ? html`<p class="board-note">
          <b>Withheld for want of an input:</b> ${cover.withheld.join(', ')}. These are not
          findings of nil; they are findings not made.
        </p>`
      : ''}

    ${!showBasis ? '' : html`<h2>Basis of comparison</h2>`}
    ${!showBasis
      ? ''
      : basis
      ? html`
          <table class="board-table">
            <tbody>
              <tr><td>Corridor</td><td>${basis.corridor}${basis.mode ? ` · ${basis.mode}` : ''}</td></tr>
              <tr>
                <td>Band</td>
                <td class="num">
                  ${money(basis.low, basis.currency)} low · ${money(basis.median, basis.currency)}
                  median · ${money(basis.high, basis.currency)} high, per tonne-km
                </td>
              </tr>
              <tr>
                <td>Assumed payload</td>
                <td class="num">${basis.payloadTonnes !== null ? `${basis.payloadTonnes} t` : 'Not stated'}</td>
              </tr>
              <tr>
                <td>Figures measured</td>
                <td class="num">${basis.measuredAt || 'Not separately recorded'}</td>
              </tr>
              <tr><td>Last checked</td><td class="num">${basis.checkedAt || 'Not recorded'}</td></tr>
              <tr>
                <td>Provenance</td>
                <td>${basis.provenance ? PROVENANCE_LABEL[basis.provenance] || basis.provenance : 'Not stated'}</td>
              </tr>
            </tbody>
          </table>
          ${basis.basis
            ? html`<p class="board-note"><b>How the band was struck.</b> ${basis.basis}</p>`
            : ''}
          ${basis.payloadTonnes !== null &&
          Number.isFinite(input.payload) &&
          Math.abs(input.payload - basis.payloadTonnes) / basis.payloadTonnes > 0.1
            ? html`<p class="board-note">
                <b>Read the comparison with care.</b> This run is priced at ${input.payload} t
                against a band assuming ${basis.payloadTonnes} t. Cost per tonne-km spreads the
                trip over the tonnes carried, so part of any gap is utilisation rather than the
                lane.
              </p>`
            : ''}
        `
      : html`<p class="board-note">
          This run was not compared against a published band — no corridor benchmark applied to
          the inputs given. The cost figures below stand on their own and are not a judgement
          about whether the lane is priced well.
        </p>`}

    <h2>${options.contentHeading || 'Findings'}</h2>
  `;

  return node;
}

/** Footnote naming the document, so a loose page can be traced back. */
export function boardFootnote(result, options = {}) {
  const reference = documentReference(
    options.input || result?.input || {},
    options.prefix || 'KLA',
    options.register || CORRIDOR_RUN_REGISTER
  );
  return (
    `${reference ? `${reference} · ` : ''}Produced with Kanda Logistics Advisory. Every figure ` +
    'is computed on the reader’s own device from values supplied by the operator; nothing was ' +
    'transmitted and no figure here came from us. Benchmarks carry the provenance and dates ' +
    'shown against them above. This document is decision support, not professional advice.'
  );
}
